import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { OrganizationLayout } from '../../../components/OrganizationLayout';
import { WorkLanguageSettings } from '../../../components/WorkLanguageSettings';
import { WorkingHoursForm } from '../../../components/WorkingHoursForm';
import { SSOSettings } from '../../../components/SSOSettings/SSOSettings';
import { useGetOrganizationQuery } from '../../../hooks/queries/useGetOrganizationQuery';
import { useGetOrganizationSchedulingSettings } from '../../../hooks/queries/useGetOrganizationSchedulingSettings';
import { useUpdateOrganizationSchedulingSettingsMutation } from '../../../hooks/mutations/useUpdateOrganizationSchedulingSettingsMutation';
import { SYSTEM_DEFAULTS, type BaseSchedulingSettings } from '../../../api/types/scheduling-settings.types';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';

/** Felt organisasjonen ikke har satt, får systemets standardverdi */
const withDefaults = (settings?: Partial<Record<keyof BaseSchedulingSettings, unknown>> | null): BaseSchedulingSettings => ({
  workdayStartHour: (settings?.workdayStartHour as number | null | undefined) ?? SYSTEM_DEFAULTS.workdayStartHour,
  workdayStartMinute: (settings?.workdayStartMinute as number | null | undefined) ?? SYSTEM_DEFAULTS.workdayStartMinute,
  workdayEndHour: (settings?.workdayEndHour as number | null | undefined) ?? SYSTEM_DEFAULTS.workdayEndHour,
  workdayEndMinute: (settings?.workdayEndMinute as number | null | undefined) ?? SYSTEM_DEFAULTS.workdayEndMinute,
  skipWeekends: (settings?.skipWeekends as boolean | null | undefined) ?? SYSTEM_DEFAULTS.skipWeekends,
  skipHolidays: (settings?.skipHolidays as boolean | null | undefined) ?? SYSTEM_DEFAULTS.skipHolidays,
  holidayCountryCode: (settings?.holidayCountryCode as string | null | undefined) ?? SYSTEM_DEFAULTS.holidayCountryCode,
});

/** Innstillinger for organisasjonen: arbeidsspråk, arbeidstid og innlogging med Microsoft */
export const OrganizationSettingsPage = () => {
  const { t } = useTranslation();
  const { hasFeature } = useFeatureFlag();

  const { data: organization, isLoading: isLoadingOrg, error: orgError } = useGetOrganizationQuery();
  const {
    data: schedulingSettings,
    isLoading: isLoadingScheduling,
    error: schedulingError,
  } = useGetOrganizationSchedulingSettings();

  const schedulingSaveMutation = useUpdateOrganizationSchedulingSettingsMutation();

  useEffect(() => {
    if (schedulingSaveMutation.isSuccess) {
      toast.success(t('scheduling.page.successSaved'), { position: 'bottom-right' });
    }
  }, [schedulingSaveMutation.isSuccess, t]);

  const content = () => {
    if (isLoadingOrg || isLoadingScheduling) {
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t('organizationSettings.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={220} />
        </div>
      );
    }

    if (orgError || schedulingError || !organization) {
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('organizationSettings.errorTitle')}
          </Heading>
          <Paragraph>{t('organizationSettings.errorMessage')}</Paragraph>
        </Alert>
      );
    }

    return (
      <>
        <WorkLanguageSettings initialWorkLanguage={organization.workLanguage} />
        <WorkingHoursForm
          effectiveSettings={withDefaults(schedulingSettings)}
          saveMutation={schedulingSaveMutation}
          description={t('organizationSettings.scheduling.description')}
        />
        {hasFeature('SSO') && <SSOSettings />}
      </>
    );
  };

  return <OrganizationLayout>{content()}</OrganizationLayout>;
};
